import * as _Footer from '#~/common/editor/footer/index.js';
import { post } from '#~/notifications/utils.js';
import { DialogClose } from '@radix-ui/react-dialog';
import { Callout } from '@studio/design-system/callout';
import * as Dialog from '@studio/design-system/dialog';
import { Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import * as Stepper from '@studio/design-system/stepper';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useRef, useState } from 'react';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { ItemsList } from '../components/index.js';

export interface Network {
  name: string;
  address: string;
  port: number;
  type: string;
}
interface Props {
  networks: Network[];
  onAddDevice: (ip: string, port: number) => Promise<void>;
  onRemoveDevice: (port: number) => Promise<unknown>;
  onSearch: () => void;
  onClose: () => void;
  isConnected: boolean;
}
export const ModalUI = ({
  networks,
  onSearch,
  onAddDevice,
  onRemoveDevice,
  onClose,
  isConnected
}: Props) => {
  const list = networks.map((network) => ({
    id: network.address,
    left: network.address,
    right: network.name
  }));
  const [item, setItem] = useState<T_Maybe<(typeof list)[number]>>();
  const [selectedItem, setSelectedItem] = useState(() => {
    if (list.length === 1) return list[0];
    return undefined;
  });
  const selectedNetwork = networks.find((v) => v.address === selectedItem?.id);
  const [step, setStep] = useState<1 | 2>(1);
  // The port THIS dialog launched a bridge on (set when Continue starts it).
  // A ref, not state: the cancel-cleanup reads an imperative fact, not render
  // state, and must not depend on a re-render having flushed first.
  const launchedPortRef = useRef<number | undefined>(undefined);

  const content = (() => {
    if (!selectedNetwork) {
      return (
        <div>
          <Callout title='Multiple networks detected on your computer.' severity='info'>
            <Callout.P>
              Your computer is connected to multiple networks. Please select the network address
              that matches the subnet of your IP controller. This ensures the controller can
              communicate properly with {vendorName()}.
            </Callout.P>
          </Callout>
          <ItemsList
            className='mt-24'
            copy={{
              leftTitle: 'IP Address',
              rightTitle: 'Type',
              noItemsCopy: 'No Networks found.'
            }}
            list={list}
            selected={item}
            setSelected={(v) => setItem({ ...v, right: v.right ?? '' })}
            search={{
              copy: 'Search Again',
              found: `${networks.length} networks found`,
              onSearch
            }}
          />
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className='flex flex-col gap-40'>
          <Callout title='You must proceed in your controllers UI' severity='info'>
            <Callout.P>
              Please add the following IP Address and Port Number as a camera into your controller.
              After adding {vendorName()} as a camera, proceed to the next step to confirm
              connection.
            </Callout.P>
          </Callout>

          <div className='flex flex-col gap-24'>
            <div className='text-base'>Studio Connection</div>

            <div className='flex flex-col gap-16'>
              <InputCopy value={selectedNetwork?.address} label={`${vendorName()} IP Address`} />
              <InputCopy
                value={selectedNetwork?.port}
                type='number'
                label={`${vendorName()} Port Number`}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='mx-40 mt-20 flex flex-col gap-20 text-center'>
        <h1>Test connection</h1>
        <p className='text-muted'>
          To confirm your controller's connection with {vendorName()}, start using your controller.{' '}
          {vendorName()} will confirm the connection once it receives a command.
        </p>
        {!isConnected && <div className='text-primary'>Waiting for command...</div>}
        {isConnected && (
          <div className='flex flex-col items-center gap-20 text-success'>
            <i className='i i-mdi-tick-circle text-[75px]' />
            Connection successful!
          </div>
        )}
      </div>
    );
  })();

  const footer = (() => {
    if (!selectedNetwork) {
      return (
        <>
          <div />
          <_Footer.Next
            label='Continue'
            disabled={!item}
            onClick={() => {
              setSelectedItem(item);
            }}
          />
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          <div />
          <_Footer.Next
            label='Continue'
            disabled={!selectedItem}
            onClick={async () => {
              setStep(2);
              // Record the port we are about to launch so the cancel path tears
              // down only this dialog's own bridge, never a saved row it aliases.
              launchedPortRef.current = selectedNetwork.port;
              await onAddDevice(selectedNetwork.address, selectedNetwork.port);
            }}
          />
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <_Footer.Back label='Back' onClick={() => setStep(1)} />
          <DialogClose>
            <_Footer.Next disabled={!isConnected} label='Save and Finish' onClick={onClose} />
          </DialogClose>
        </>
      );
    }
    return null;
  })();

  return (
    <Dialog.Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      onBeforeClose={() => {
        // Per settle-every-terminal-branch (ai.knowledge/global): the cancel path
        // is a terminal that must clean up ONLY a bridge this dialog itself
        // launched. Deleting `selectedNetwork.port` unconditionally deleted a
        // saved-but-idle controller whose port the Add dialog was merely offered.
        // Only the launched port is torn down; a dialog that never launched never
        // issues the generic saved-row delete.
        const launchedPort = launchedPortRef.current;
        if (!isConnected && launchedPort !== undefined) {
          onRemoveDevice(launchedPort);
        }
      }}
    >
      <Dialog.Overlay>
        <Dialog.Panel className='flex min-h-[800px] w-dialog flex-col'>
          <Dialog.Header title='Add IP Controller' />
          <Dialog.Content
            style={{ pointerEvents: 'auto' }}
            className='flex flex-grow flex-col gap-40 px-40 py-20'
          >
            <Stepper.Root step={step}>
              <Stepper.Step icon='joystick' label='Connect to source' />
              <Stepper.Step icon='move' label='Connect controls' />
            </Stepper.Root>
            {/* <pre>{JSON.stringify({ selectedNetwork, item, step, isConnected }, null, 2)}</pre> */}
            {content}
          </Dialog.Content>
          <Dialog.Footer>{footer}</Dialog.Footer>
        </Dialog.Panel>
      </Dialog.Overlay>
    </Dialog.Dialog>
  );
};

function InputCopy({
  value,
  type = 'text',
  label = ''
}: {
  value?: string | number | undefined;
  label: string;
  type?: 'number' | 'text';
}) {
  const copyContent = async () => {
    try {
      if (value === undefined) return;
      await navigator.clipboard.writeText(value + '');
      post({
        message: (
          <div>
            <strong className='font-demibold'>{label}</strong> copied to clipboard.
          </div>
        )
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };
  return (
    <Group label={label}>
      <div className='flex items-center gap-8'>
        <Mediator type={type} readOnly value={value} className='w-[240px]' />
        <button
          className='cursor-pointer underline hover:opacity-75 disabled:opacity-75'
          onClick={copyContent}
          disabled={value === undefined}
        >
          Copy
        </button>
      </div>
    </Group>
  );
}
